<template>
  <section id="pricing" class="pricing-section" aria-labelledby="pricing-title">
    <div class="pricing-container">
      <div class="pricing-header" data-reveal="up">
        <span class="section-label">{{ $t('plans_page.label') }}</span>
        <h2 id="pricing-title" class="section-title">
          {{ $t('plans_page.title_pre') }}
          <span class="gradient-text">{{ $t('plans_page.title_highlight') }}</span>
        </h2>
        <p class="section-subtitle">{{ $t('plans_page.subtitle') }}</p>
      </div>

      <!-- Plans grid -->
      <div class="pricing-grid-wrapper" data-reveal="up">
        <PricingGrid
          :show-button="showButtons"
          :loading="subscribing !== ''"
          @subscribe="subscribe"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const props = defineProps({
  showButtons: {
    type: Boolean,
    default: true
  }
})

const { scan } = useScrollReveal()
const localePath = useLocalePath()
const subscribing = ref('')

const { loggedIn } = useUserSession()

const subscribe = async (plan: any) => {
  // Not logged in → sign up first; the checkout endpoint needs a session, and
  // the user picks a plan again once authenticated. Applies to ALL tiers.
  if (!loggedIn.value) {
    await navigateTo(localePath('/auth') + '?tab=signup')
    return
  }
  // The free tier never touches Stripe — every signed-up account already has
  // it. Send them into the studio instead of into a €0 checkout.
  if (Number(plan.price) === 0) {
    window.location.href = getSubdomainUrl('creator', '/')
    return
  }
  subscribing.value = plan.name
  try {
    const { url } = await $fetch('/api/stripe/create-checkout-session', {
      method: 'POST',
      // billingInterval comes from the grid's monthly/yearly toggle.
      body: { planName: plan.name, price: plan.price, interval: plan.billingInterval || 'month' }
    })
    if (url) {
      window.location.href = url
    }
  } catch (e) {
    ElNotification({
      title: 'Error',
      message: (e as any).data?.statusMessage || 'Failed to start checkout',
      type: 'error'
    })
  } finally {
    subscribing.value = ''
  }
}

onMounted(() => {
  // Re-scan after cards render so scroll-reveal picks them up
  nextTick(() => {
    scan()
  })
})
</script>

<style scoped>
/* Mobile-first. Ladder: 480 / 640 / 768 / 1024 / 1280 (variables.css). */
.pricing-section {
  position: relative;
  padding: var(--landing-section-pad) 0;
  background:
    radial-gradient(circle at 50% 30%, rgb(var(--secondary-channels) / 0.04) 0%, transparent 60%),
    var(--bg-color);
}

.pricing-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-6);
}

.pricing-header {
  text-align: center;
  margin-bottom: clamp(40px, 5vw, 64px);
}

.section-label {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--primary);
  margin-bottom: var(--space-4);
}

.section-title {
  font-family: var(--font-heading);
  font-size: var(--landing-title-size);
  font-weight: 800;
  line-height: 1.1;
  color: #fff;
  margin-bottom: var(--space-4);
  text-wrap: balance;
}

.gradient-text {
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.section-subtitle {
  font-size: var(--landing-sub-size);
  color: var(--text-muted);
  max-width: 480px;
  margin: 0 auto;
}

.pricing-grid-wrapper {
  width: 100%;
}
</style>
